import type { ReactNode } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';

interface HeaderProps {
  leading?: ReactNode;
  trailing?: ReactNode;
  title?: ReactNode;
  height?: number;
  showTopInset?: boolean;
  style?: ViewStyle;
}

// Solid fill matching colors.background.primary so the Header reads as the
// exact same tone as the root SafeAreaView and the BottomNav.
const HEADER_FILL = colors.background.primary;

export function Header({
  leading,
  trailing,
  title,
  height = 56,
  showTopInset = true,
  style,
}: Readonly<HeaderProps>) {
  const insets = useSafeAreaInsets();
  const topPadding = showTopInset ? insets.top : 0;

  return (
    <View
      style={[
        styles.shell,
        {
          height: height + topPadding,
          paddingTop: topPadding,
          backgroundColor: HEADER_FILL,
        },
        style,
      ]}
    >
      <View style={[styles.row, { height }]}>
        <View style={styles.slot} accessibilityRole="none">
          {leading}
        </View>
        <View style={styles.titleSlot} pointerEvents="box-none">
          {title}
        </View>
        <View style={[styles.slot, styles.trailing]} accessibilityRole="none">
          {trailing}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.md,
  },
  slot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    minWidth: 44,
  },
  trailing: {
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  titleSlot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
